import {
  ReportEmpty,
  ReportError,
  ReportLoading,
  ReportMetricCard,
  ReportPageHeader,
  formatQuantity,
} from "@/components/reports/report-page";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { buildMetadata } from "@/lib/metadata";
import { ReportService } from "@/services/report-service";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Box, MessageCircle } from "lucide-react";
import { normalizeReportSearch, reportSearchSchema } from "./-report-search";

export const Route = createFileRoute("/_main/reports/oos")({
  validateSearch: reportSearchSchema,
  component: OOSReportRoute,
  head: () => ({
    meta: buildMetadata({
      title: "OOS Report",
      description: "Review orders affected by products currently out of stock",
      keywords: ["reports", "inventory", "out of stock"],
    }),
  }),
});

function OOSReportRoute() {
  const range = normalizeReportSearch(Route.useSearch());
  const navigate = Route.useNavigate();
  const reportQuery = useQuery({
    queryKey: ["reports", "oos", range.dateFrom, range.dateTo],
    queryFn: () => ReportService.getOOS(range),
  });
  const report = reportQuery.data;

  return (
    <main className="space-y-7 p-4 md:p-8">
      <ReportPageHeader
        title="OOS Report"
        totalOrders={report?.out_of_stock_orders}
        range={range}
        onRangeChange={(nextRange) =>
          navigate({ search: nextRange, replace: true })
        }
      />

      {reportQuery.isLoading ? <ReportLoading /> : null}
      {reportQuery.isError ? <ReportError /> : null}

      {report ? (
        <>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm">
            This is a current-state report: it checks today&apos;s
            available-for-sale stock against orders in the selected period.
          </div>

          <section className="grid gap-5 md:grid-cols-3">
            <ReportMetricCard
              label="OOS Orders"
              value={report.out_of_stock_orders}
              icon={AlertTriangle}
              iconClassName="text-amber-500"
            />
            <ReportMetricCard
              label="Affected SKUs"
              value={report.affected_skus}
              icon={Box}
            />
            <ReportMetricCard
              label="Top Channel"
              value={report.top_channel ?? "—"}
              icon={MessageCircle}
            />
          </section>

          <Card className="gap-0 overflow-hidden py-0 shadow-sm">
            <CardHeader className="py-5">
              <CardTitle className="font-medium text-base">
                Affected order items ({report.rows.length})
              </CardTitle>
            </CardHeader>
            {report.rows.length ? (
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>Available for sale</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.rows.map((row, index) => (
                    <TableRow key={`${row.order_id}-${row.sku}-${index}`}>
                      <TableCell className="font-medium">
                        #{row.order_number}
                      </TableCell>
                      <TableCell>{row.customer}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{row.channel}</Badge>
                      </TableCell>
                      <TableCell>
                        <div>{row.product}</div>
                        <div className="text-muted-foreground text-xs">
                          {row.sku}
                        </div>
                      </TableCell>
                      <TableCell>
                        {row.requested_quantity == null
                          ? "—"
                          : `${formatQuantity(row.requested_quantity)} ${row.uom ?? ""}`}
                      </TableCell>
                      <TableCell className="text-red-600">
                        {row.available_for_sale == null
                          ? "Unknown"
                          : formatQuantity(row.available_for_sale)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <CardContent className="pb-6">
                <ReportEmpty>No current out-of-stock items found.</ReportEmpty>
              </CardContent>
            )}
          </Card>
        </>
      ) : null}
    </main>
  );
}
